import { Injectable, signal, inject } from '@angular/core';
import { Topic, DEFAULT_TOPICS } from '../models/topic.model';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class TopicService {
  private readonly supa = inject(SupabaseService);
  private readonly auth = inject(AuthService);
  private _topics = signal<Topic[]>(DEFAULT_TOPICS);
  readonly topics = this._topics.asReadonly();

  constructor() {
    this.auth.waitForInit().then(() => {
      if (this.auth.isAuthenticated()) this._loadFromSupabase();
    });
  }

  private async _loadFromSupabase(): Promise<void> {
    const topics = await this.supa.loadTopics();
    if (topics.length > 0) {
      this._topics.set(topics);
    } else {
      // First run: seed defaults
      for (const t of DEFAULT_TOPICS) {
        await this.supa.upsertTopic(t);
      }
    }
  }

  getByKey(key: string): Topic | undefined {
    return this._topics().find(t => t.key === key);
  }

  addTopic(topic: Omit<Topic, 'active'>): void {
    const full: Topic = { ...topic, hashtags: (topic as any).hashtags ?? [], active: true };
    this._topics.update(ts => [...ts, full]);
    this.supa.upsertTopic(full);
  }

  updateTopic(key: string, changes: Partial<Topic>): void {
    this._topics.update(ts => ts.map(t => t.key === key ? { ...t, ...changes } : t));
    const updated = this._topics().find(t => t.key === key);
    if (updated) this.supa.upsertTopic(updated);
  }
}
